/**
 * @module lib/election/live-results-table
 */
import tgamConfig from "config/tgam";
import { getProp, qs } from "utils/utils";
import ee from "lib/eventemitter";
import { getEntriesByName, mark, measure } from "../performance/user-timing";
import { getStatus } from "./lead-data-helpers";
import logger from "utils/logger";
import { errorStateHideFeature, errorStateShowFeature } from "lib/election/error-state";
import { getParty, getNumDifference } from "./helpers";

const debugTag = "[ELECTION-LIVE-RESULTS-TABLE]";
let dataTable = null; // live results table wrapper container
let container = null; // live results table body container

const electionYear = getProp(tgamConfig, "meta.electionYear", "2021");

const partyMappings = {
  LIB: {
    name: "Liberal",
    abbr: "lib",
    modifier: "lib",
  },
  CON: {
    name: "Conservative",
    abbr: "con",
    modifier: "cpc"
  },
  NDP: {
    name: "NDP",
    abbr: "ndp",
    modifier: "ndp"
  },
  BQ: {
    name: "Bloc Québécois",
    abbr: "bq",
    modifier: "bq"
  },
  GRN: {
    name: "Green",
    abbr: "grn",
    modifier: "grn"
  },
  Others: {
    name: "Others",
    abbr: "oth",
    modifier: "oth"
  }
};

if (electionYear === "2019") { // PPC was listed as "PPC" in the CP feed for the 2019 election.
  partyMappings.PPC = {
    name: "People's",
    abbr: "ppc",
    modifier: "ppc"
  };
} else { // In 2021, CP returned the People's Party results as "PP".
  partyMappings.PP = {
    name: "People's",
    abbr: "ppc",
    modifier: "ppc"
  };
}

/**
 * @method init
 * @description Initialize the live results table.
 */
export default function init() {
  dataTable = qs(".js-election-live-results-table-wrapper");
  container = qs(".js-election-live-results-table");
  if (!dataTable) {
    return;
  }

  ee.on("election:leading-data-failed", () => {
    errorStateHideFeature(dataTable);
  });

  ee.on("election:leading-data", data => {
    if (!data) {
      return;
    }
    logger.info(`${debugTag} Data: `, data);
    const status = getStatus(data);
    errorStateShowFeature(dataTable);
    if (status !== "") {
      dataTable.classList.remove("u-hidden");
    }
    let leadingElectedPartyData = [...getProp(data, "Leading.Party", [])];

    leadingElectedPartyData.sort((resultA, resultB) => compareResults(resultA, resultB));
    mark("live-results_started");
    populateTable(leadingElectedPartyData, status);
    mark("live-results_done");
    measure("live-results");
    logger.info(`${debugTag} Performance measure result: `, getEntriesByName("live-results"));
  });
}

/**
 * Populates leading + elected results numbers for all parties.
 * @param {Array<Object>} leadingElectedData - array of JSON objects with leading and elected party data, from highest to lowest
 * @param {String} status - status of election (i.e., "" for no results, "partial", and "complete")
 */
function populateTable(leadingElectedData, status) {
  logger.info(`${debugTag} status: `, status);
  logger.info(`${debugTag} leadingElectedData: `, leadingElectedData);

  const electionComplete = status === "complete";
  let listItems = "";

  leadingElectedData.forEach((partyResult) => {
    const partyName = getProp(partyResult, "Name").toLowerCase();
    const { previousElection = 0 } = getParty(partyName, electionYear);
    const leadingElectedCount = Number(getProp(partyResult, "LeadingElected", ""));
    const electedCount = Number(getProp(partyResult, "Elected", ""));
    const popCount = Number(Math.round(getProp(partyResult, "Pop", "")));
    const changeInSeats = electionComplete ? leadingElectedCount - previousElection : getNumDifference(partyResult);
    const partyInfo = partyMappings[partyResult.Name];
    listItems += getPartyResultItem(partyInfo.modifier, partyInfo.abbr, partyInfo.name, leadingElectedCount, electedCount, popCount, changeInSeats);
  });

  if (container) {
    container.innerHTML = listItems;
  }
}

/**
 * Returns leading + elected party result list item, consisting of party name and number of seats.
 * @param {String} modifier - modifier class for party
 * @param {String} abbr - abbreviated version of party name
 * @param {String} partyName - party name
 * @param {Number} [seats=0] - number of "seats" (i.e., leading + elected)
 * @param {Number} [elected=0] - number of "seats elected"
 * @param {Number} [pop=0] - percentage of popular vote
 * @param {Number} [change=0] - change in seats (gain - loss)
 * @returns {String} list item for live results
 */
function getPartyResultItem(modifier, abbr, partyName, seats = 0, elected = 0, pop = 0, change = 0) {
  const changeMarkup = change !== 0 ? `<span class='c-election-table__${change > 0 ? "increase'>+" : "decrease'>"}${change}</span>` : change;
  return `<tr class="c-election-table__body-row c-election-table__body-row--${modifier}">
            <td class="c-election-table__body-cell c-election-table__body-cell--party-full">
              <span class="c-election-table__body-cell-content">${partyName}</span>
            </td>
            <td class="c-election-table__body-cell c-election-table__body-cell--party-abbr">
              <span class="c-election-table__body-cell-content"><abbr class="c-election-table__abbr" title="${partyName}">${abbr}</abbr></span>
            </td>
            <td class="c-election-table__body-cell c-election-table__body-cell--leading">
              <span class="c-election-table__body-cell-content">${seats}</span>
            </td>
            <td class="c-election-table__body-cell c-election-table__body-cell--seats">
              <span class="c-election-table__body-cell-content">${elected}</span>
            </td>
            <td class="c-election-table__body-cell c-election-table__body-cell--popular">
              <span class="c-election-table__body-cell-content">${pop}%</span>
            </td>
            <td class="c-election-table__body-cell c-election-table__body-cell--change">
              <span class="c-election-table__body-cell-content">${changeMarkup}</span>
            </td>
          </tr>`;
}

/**
 * Compares resultA's leading + elected seats with resultB's leading + elected seats.
 * @param {Object} resultA - partyA's leading + elected results
 * @param {Object} resultB - partyB's leading + elected results
 * @returns {Number} -1 if resultA seats > result B seats, 1 if resultA seats < resultB seats, 0 if equal
 */
function compareResults(resultA, resultB) {
  const numSeatsPartyA = Number(resultA.LeadingElected);
  const numSeatsPartyB = Number(resultB.LeadingElected);

  if (!Number.isNaN(numSeatsPartyA) && !Number.isNaN(numSeatsPartyB)) {
    if (numSeatsPartyA > numSeatsPartyB) {
      return -1;
    }

    if (numSeatsPartyA < numSeatsPartyB) {
      return 1;
    }
  }

  return 0;
}
